import { createContext, ReactNode, useContext, useEffect, useMemo, useState } from 'react';
import { ApolloClient, useQuery } from '@apollo/client';
import { useLocale } from '../../../../../contexts/LocaleProvider';
import {
    GET_PRODUCT_APPLICATIONS_QUERY,
    getProductApplicationsQueryVariables,
} from '../queries/getProductApplicationsQuery';
import { ProductApplication } from '../types/ProductApplication';
import { transformProductApplicationSegmentResponse } from '../tools/transformProductApplicationSegmentResponse';
import { GetProductApplicationSegmentListingQuery } from '../schemas/generated-types/graphql';
import { isProductApplicationSegment } from '../guards/productApplication';
import { useQueryFallbackResponse } from '../../../common/hooks/useQueryFallbackResponse';

type ProductApplicationById = Record<string, ProductApplication>;
type ProductApplicationByKey = Record<string, ProductApplication>;

interface ProductApplicationsContextValue {
    productApplications: ProductApplication[];
    productApplicationById: ProductApplicationById;
    productApplicationByKey: ProductApplicationByKey;
    loading: boolean;
    error: any;
    initialize: () => void;
}

const ProductApplicationsContext = createContext<ProductApplicationsContextValue | undefined>(
    undefined
);

interface ProductApplicationsProviderProps {
    children: ReactNode;
    apolloClient: ApolloClient<any>;
}

const ProductApplicationsProvider = ({
    children,
    apolloClient,
}: ProductApplicationsProviderProps) => {
    const { locale } = useLocale();

    const [applicationById, setApplicationById] = useState<ProductApplicationById>({});
    const [applicationByKey, setApplicationByKey] = useState<ProductApplicationByKey>({});

    const [initialized, setInitialized] = useState(false);

    const query = GET_PRODUCT_APPLICATIONS_QUERY;
    const {
        loading: loadingQuery,
        error: queryError,
        data: queryData,
    } = useQuery<GetProductApplicationSegmentListingQuery>(query, {
        variables: getProductApplicationsQueryVariables(locale),
        client: apolloClient,
        errorPolicy: 'all', // Allow partial data instead of full rejection
        skip: !initialized, // Do not run query until initialization is triggered.
    });

    const {
        fallbackData,
        loading: loadingFallback,
        error: fallbackError,
    } = useQueryFallbackResponse({ query, locale, initialized, queryError: queryError });

    const applications = useMemo(() => {
        const data = queryData || fallbackData;
        if (!data) return [];
        const applications = transformProductApplicationSegmentResponse(data);

        const sortTreeByName = (items: ProductApplication[]): ProductApplication[] =>
            items
                .slice()
                .sort((a, b) => a.name.localeCompare(b.name, locale))
                .map((item) => {
                    if (isProductApplicationSegment(item)) {
                        return {
                            ...item,
                            children: sortTreeByName(item.children),
                        };
                    }
                    return item;
                });

        return sortTreeByName(applications);
    }, [queryData, fallbackData, locale]);

    useEffect(() => {
        const applicationById: ProductApplicationById = {};
        const applicationByKey: ProductApplicationByKey = {};

        applications.forEach((application) => {
            applicationById[application.id] = application;
            applicationByKey[application.key] = application;

            application.children?.forEach((child) => {
                applicationById[child.id] = child;
                applicationByKey[`${application.key}/${child.key}`] = child;
            });
        });

        setApplicationById(applicationById);
        setApplicationByKey(applicationByKey);
    }, [applications]);

    const initialize = () => {
        if (!initialized) {
            setInitialized(true);
        }
    };

    const loading = loadingQuery || loadingFallback;
    // if we have fallback data, we don't consider it an error
    const error = fallbackError || (queryError && !fallbackData ? queryError : null);

    return (
        <ProductApplicationsContext.Provider
            value={{
                productApplications: applications,
                productApplicationById: applicationById,
                productApplicationByKey: applicationByKey,
                loading,
                error,
                initialize,
            }}
        >
            {children}
        </ProductApplicationsContext.Provider>
    );
};

// Unsafe hook: does not trigger initialization.
function useProductApplicationsUnsafe() {
    const context = useContext(ProductApplicationsContext);
    if (!context) {
        throw new Error(
            'useProductApplicationsUnsafe must be used within an ProductApplicationsProvider'
        );
    }
    return context;
}

// Safe hook: triggers lazy initialization when a consumer uses it.
function useProductApplications() {
    const context = useProductApplicationsUnsafe();
    // Trigger lazy initialization when a consumer uses this hook.
    useEffect(() => {
        context.initialize();
    }, [context]);
    return context;
}

export { ProductApplicationsProvider, useProductApplications, useProductApplicationsUnsafe };
export type { ProductApplicationsContextValue, ProductApplicationById, ProductApplicationByKey };
